import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  CheckIcon,
  ChevronDownIcon,
  FilterLinesIcon,
  SearchSmIcon,
  Trash04Icon,
  XIcon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress, Option, Select } from '@mui/joy';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useDebounce } from 'use-debounce';

import { REVISION_STATUS_DEPLOY_AVAILABLE } from '@/Pages/HostProject/constants';
import { useCopyAction } from '@/components/CopyButton/useCopy';
import { DateTimeRangePicker } from '@/components/DateTimeRangePicker/DateTimeRangePicker';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import {
  HostRevisionStatusName,
  getRevisionSimpleStatus,
} from '@/components/HostRevisionStatus/HostRevisionStatus';
import { PageTitle } from '@/components/PageTitle';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import {
  DateTimeRangePickerValue,
  parseRelativeDate,
} from '@/components/RunsTable/utils/getDateTimeRangeLabel';
import { Button, IconButton, Input, Text, Tooltip } from '@/design-system';
import { useHostRevision, useHostRevisionLogsInfinite } from '@/hooks/useSwr';
import CopyIcon from '@/icons/CopyIcon.svg?react';
import RocketShipIcon from '@/icons/RocketShip.svg?react';
import { HostRevisionLogsRequest } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { useDeploymentFormType } from '../HostProjects/hooks/useDeploymentFormType';
import { LogLevel } from './types';

dayjs.extend(utc);

const getLogsFilter = (
  timeModel: DateTimeRangePickerValue
): HostRevisionLogsRequest => {
  let startDuration;
  let startTime = timeModel?.start_time;
  const endTime = timeModel?.end_time;

  if (timeModel?.duration) {
    startDuration = parseRelativeDate(timeModel.duration);

    if (startDuration) {
      startTime = dayjs
        .utc()
        .subtract(startDuration.value, startDuration.unit)
        .toISOString();
    }
  }
  return { limit: 200, start_time: startTime, end_time: endTime };
};

function LogsFilterBar({
  traceId,
  onApply,
}: {
  traceId: string | null;
  onApply: (traceId: string | null) => void;
}) {
  const [inputValue, setInputValue] = useState(traceId || '');

  const handleClear = () => {
    setInputValue('');
    onApply(null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Filters</span>
        {traceId && (
          <IconButton
            variant="plain"
            label="Clear Filters"
            onClick={handleClear}
            color="secondary"
            icon={Trash04Icon}
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <Text variant="xs">Trace ID:</Text>
        <Input
          className="flex-1"
          value={inputValue}
          onChange={(val) => setInputValue(val)}
          size="sm"
        />
      </div>
      <div className="flex justify-end">
        <Button
          color="primary"
          size="sm"
          onClick={() => onApply(inputValue.trim() || null)}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

const LogLevelSelector = ({
  logLevel,
  setLogLevel,
}: {
  logLevel: LogLevel;
  setLogLevel: (level: LogLevel) => void;
}) => {
  const renderLogLevelButton = (level: LogLevel) => {
    return (
      <Option value={level} label={level} color="neutral">
        <div className="flex w-full items-center gap-2 capitalize">
          {level.toLowerCase()}
          {level === logLevel && <CheckIcon className="ml-auto size-4" />}
        </div>
      </Option>
    );
  };

  return (
    <Select
      className="w-full"
      value={logLevel}
      onChange={(_, value) => setLogLevel(value as LogLevel)}
      indicator={<ChevronDownIcon className="size-4" />}
      size="sm"
      renderValue={(value) => (
        <span className="text-sm capitalize">
          Log level{value?.value ? `: ${value.value.toLowerCase()}` : ''}
        </span>
      )}
      color="neutral"
    >
      {Object.values(LogLevel).map(renderLogLevelButton)}
    </Select>
  );
};

function HostRevisionPaginatedLogs(props: {
  type: 'deploy_logs' | 'build_logs';
  shouldRevalidate: 'slow' | 'fast';
  hostProjectId: string;
  revisionId: string;
  isPending: boolean;
  stale: boolean;
  setStale: (stale: boolean) => void;
  traceIdForLogs: string | null;
  setTraceIdForLogs: (traceId: string | null) => void;
}) {
  const LOG_LIMIT = 100;

  // get time of last fetch of logs and check if we need to refetch most recent page
  const [lastFetchTime, setLastFetchTime] = useState(dayjs.utc());
  const [firstPageStale, setFirstPageStale] = useState(props.stale);
  useEffect(() => {
    const interval = setInterval(() => {
      const timeNow = dayjs.utc();
      const staleTime = props.shouldRevalidate === 'fast' ? 3 : 10;
      const isStale =
        timeNow.diff(lastFetchTime, 'seconds') > staleTime || props.stale;
      setFirstPageStale(isStale);
    }, 5000);
    return () => clearInterval(interval);
  }, [props.shouldRevalidate, lastFetchTime, props.stale]);

  const [isAtTop, setIsAtTop] = useState(true);

  // filters
  const [timeModel, setTimeModel] =
    useState<DateTimeRangePickerValue>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [logLevel, setLogLevel] = useState<LogLevel>(LogLevel.INFO);
  const [filter, setFilter] = useState<HostRevisionLogsRequest>(() =>
    getLogsFilter(timeModel)
  );

  const [filterBarOpen, setFilterBarOpen] = useState(false);

  const {
    data: logPages,
    isLoading: isLoadingLogs,
    error: errorLogs,
    isValidating: isValidatingLogs,
    mutate: mutateLogs,
    size,
    setSize,
  } = useHostRevisionLogsInfinite(
    props.type,
    props.hostProjectId,
    props.revisionId,
    {
      ...filter,
      limit: LOG_LIMIT,
      level: logLevel,
      query: debouncedSearchQuery,
      ...(props.type === 'deploy_logs' && {
        run_id: props.traceIdForLogs ?? undefined,
      }),
      order: 'desc',
    },
    {
      onSuccess: () => {
        setLastFetchTime(dayjs.utc());
        props.setStale(false);
      },
      revalidateFirstPage: false,
    }
  );
  const logs = useMemo(
    () => logPages?.flatMap((page) => page.logs) ?? [],
    [logPages]
  );
  const hasMore = !!logPages?.at(-1)?.next_offset;

  const { onCopy, copied } = useCopyAction();

  // if the user is scrolled to the top and the first page is stale, refetch the first page
  // and delete the cache for the rest of the pages but don't refetch them
  useEffect(() => {
    // we don't need to refetch build logs if the build is complete
    if (props.type === 'build_logs' && !props.isPending) return;
    const refetchFirstPageAndClearCache = async () => {
      if (isAtTop && firstPageStale) {
        // First, set size to 1 to only keep the first page in the rendered UI.
        // note: this does cause a second refetch of the first page, but only a single time (when going from n pages to 1 page)
        setSize(1);

        await mutateLogs(
          // delete cached data
          undefined,
          {
            // Update the cache with our sliced data (first page only)
            populateCache: true,
            optimisticData: (data) => data?.slice(0, 1) ?? [],
          }
        );
      }
    };
    refetchFirstPageAndClearCache();
  }, [
    isAtTop,
    firstPageStale,
    size,
    mutateLogs,
    setSize,
    props.isPending,
    props.type,
  ]);

  const renderLogs = () => {
    if (!logs.length)
      return (
        <div className="flex h-full flex-col items-center gap-2">
          <div className="text-center text-sm text-tertiary">
            No logs found for selected filters.
          </div>
        </div>
      );
    return (
      <Virtuoso
        data={logs}
        atTopStateChange={(isAtTop) => {
          setIsAtTop(isAtTop);
        }}
        components={{
          Footer: () => {
            if (!hasMore) return null;
            return (
              <LoadMoreButton
                isInitialLoad
                isLoading={isLoadingLogs || isValidatingLogs}
                onClick={() => setSize(size + 1)}
                isVirtual={true}
              />
            );
          },
        }}
        itemContent={(_, item) => (
          <div key={item.id} className="grid grid-cols-[auto,1fr] gap-4 py-0.5">
            <div className="whitespace-nowrap font-mono text-sm text-tertiary">
              {new Date(item.timestamp).toLocaleString()}
            </div>
            <div
              className={cn(
                'relative whitespace-pre font-mono text-sm',
                (item.level === 'ERROR' || item.level === 'CRITICAL') &&
                  'text-error',
                item.level === 'WARNING' && 'text-warning'
              )}
            >
              <span>[{item.level}]</span>{' '}
              <span className="whitespace-pre-wrap">{item.message}</span>
            </div>
          </div>
        )}
      />
    );
  };

  return (
    <div className="flex h-full flex-grow flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Input
            value={searchQuery}
            onChange={(val) => setSearchQuery(val)}
            placeholder="Search"
            size="sm"
            leftDecorator={<SearchSmIcon className="h-4 w-4" />}
            rightDecorator={
              <IconButton
                className={cn(
                  'h-4 w-4 border-none',
                  searchQuery ? 'visible' : 'invisible'
                )}
                color="secondary"
                onClick={() => setSearchQuery('')}
                icon={XIcon}
                label="Clear Search"
              />
            }
          />
          {props.type === 'deploy_logs' && (
            <Popover open={filterBarOpen} onOpenChange={setFilterBarOpen}>
              <PopoverTrigger asChild>
                <IconButton
                  variant={props.traceIdForLogs ? 'normal' : 'outlined'}
                  color={props.traceIdForLogs ? 'primary' : 'secondary'}
                  size="sm"
                  icon={FilterLinesIcon}
                  label="Filter logs"
                />
              </PopoverTrigger>
              <PopoverContent className="min-w-[300px] p-3">
                <LogsFilterBar
                  traceId={props.traceIdForLogs}
                  onApply={(traceId: string | null) => {
                    setFilterBarOpen(false);
                    props.setTraceIdForLogs(traceId);
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
          <LogLevelSelector logLevel={logLevel} setLogLevel={setLogLevel} />

          <DateTimeRangePicker
            value={timeModel}
            onChange={(model) => {
              setTimeModel(model);
              setFilter(getLogsFilter(model));
            }}
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-secondary px-2 py-1 text-sm font-medium transition-all hover:bg-secondary-hover active:bg-secondary disabled:pointer-events-none data-[state=open]:bg-secondary"
            disabled={isLoadingLogs || isValidatingLogs}
            onClick={() =>
              onCopy(
                logs
                  .map((log) =>
                    [
                      new Date(log.timestamp).toLocaleString(),
                      log.message,
                    ].join(' ')
                  )
                  .join('\n')
              )
            }
          >
            {copied ? (
              <>
                <CheckIcon className="size-4" />
                Copied
              </>
            ) : (
              <>
                <CopyIcon className="size-4" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {errorLogs != null && <ExpandableErrorAlert error={errorLogs} />}

      <div className="relative grid h-full flex-grow items-stretch justify-stretch rounded-lg border border-secondary bg-secondary p-3">
        {(isLoadingLogs || isValidatingLogs) && (
          <div className="absolute inset-x-0 top-0">
            <LinearProgress />
          </div>
        )}
        {renderLogs()}
      </div>
    </div>
  );
}

export function HostRevision(props: {
  revisionId: string;
  hostProjectId: string;
  traceIdForLogs: string | null;
  setTraceIdForLogs: (traceId: string | null) => void;
}) {
  const { data: revision } = useHostRevision(
    { hostProjectId: props.hostProjectId, revisionId: props.revisionId },
    {
      refreshInterval: (data) =>
        getRevisionSimpleStatus(data?.status ?? '') === 'pending' ? 3000 : 0,
    }
  );
  const deploymentFormType = useDeploymentFormType();
  const revisionStatus = getRevisionSimpleStatus(revision?.status ?? 'UNKNOWN');
  const isDeployAvailable = REVISION_STATUS_DEPLOY_AVAILABLE.includes(
    revision?.status ?? ''
  );
  const isBuildAvailable = deploymentFormType === 'cloud';
  const [stale, setStale] = useState(false);

  return (
    <div className="flex h-full flex-col gap-4">
      <PageTitle icon={<RocketShipIcon />} className="shrink-0 flex-grow-0">
        {revision?.id}
      </PageTitle>

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex flex-col items-start gap-1.5">
          <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
            Status
          </div>

          <Tooltip
            title={revision?.status_message?.trim()}
            tooltipClassName="max-w-[400px] whitespace-pre-wrap"
          >
            <div className="font-medium">
              <HostRevisionStatusName status={revision?.status} />
            </div>
          </Tooltip>
        </div>
        {revision?.updated_at != null && (
          <div className="flex flex-col items-start gap-1.5">
            <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
              Last Updated
            </div>

            <div className="font-medium">
              {new Date(revision.updated_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      <hr className="border-tertiary" />

      {(revisionStatus === 'error' || revisionStatus === 'unknown') &&
      revision?.status_message ? (
        <ExpandableErrorAlert error={revision.status_message.trim()} />
      ) : null}

      {revision && (
        <TabGroup
          defaultIndex={isDeployAvailable && isBuildAvailable ? 2 : 1}
          className="grid flex-grow grid-cols-[auto,1fr] gap-6"
        >
          <TabList
            className="flex flex-col gap-1 "
            onClick={() => setStale(true)}
          >
            {isBuildAvailable && (
              <Tab className="rounded-lg py-2 pl-3.5 pr-12 text-left font-semibold outline-none transition-all hover:bg-secondary disabled:pointer-events-none disabled:opacity-50 ui-selected:bg-secondary-hover ui-selected:hover:bg-secondary ui-not-selected:text-tertiary">
                Build
              </Tab>
            )}
            <Tab
              className="rounded-lg py-2 pl-3.5 pr-12 text-left font-semibold outline-none transition-all hover:bg-secondary disabled:pointer-events-none disabled:opacity-50 ui-selected:bg-secondary-hover ui-selected:hover:bg-secondary ui-not-selected:text-tertiary"
              disabled={!isDeployAvailable}
            >
              Server
            </Tab>
          </TabList>
          <TabPanels className="min-w-0">
            {isBuildAvailable && (
              <TabPanel className="flex h-full flex-col gap-4">
                <h2 className="text-xl font-semibold">Build Logs</h2>

                <HostRevisionPaginatedLogs
                  type="build_logs"
                  shouldRevalidate={
                    revision.status === 'BUILDING' ||
                    revision.status === 'AWAITING_BUILD'
                      ? 'fast'
                      : 'slow'
                  }
                  hostProjectId={props.hostProjectId}
                  revisionId={revision.id}
                  isPending={
                    revision.status === 'BUILDING' ||
                    revision.status === 'AWAITING_BUILD'
                  }
                  stale={stale}
                  setStale={setStale}
                  traceIdForLogs={props.traceIdForLogs}
                  setTraceIdForLogs={props.setTraceIdForLogs}
                />
              </TabPanel>
            )}
            <TabPanel className="flex h-full flex-col gap-4">
              <h2 className="text-xl font-semibold">Server Logs</h2>

              <HostRevisionPaginatedLogs
                type="deploy_logs"
                shouldRevalidate="slow"
                hostProjectId={props.hostProjectId}
                revisionId={revision.id}
                isPending={
                  revision.status === 'DEPLOYING' ||
                  revision.status === 'AWAITING_DEPLOY'
                }
                stale={stale}
                setStale={setStale}
                traceIdForLogs={props.traceIdForLogs}
                setTraceIdForLogs={props.setTraceIdForLogs}
              />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      )}
    </div>
  );
}
